import { useEffect, useState } from 'react';
import { CardRows, btnSx } from '@bumble/app/components/CardRows';
import { Loading } from '@bumble/app/components/Loading';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import { useAccount } from '@bumble/app/hooks/use-account';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { Button, Card, CardContent, Divider, Grid, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { SESSION_STORAGE_KEY } from '../constants';

export const Profile = () => {
  const api = useApi();
  const { account, isLoading, accountRefetch } = useAccount();
  const [name, setName] = useState<string>(account?.name || '');
  const [email, setEmail] = useState<string>(account?.email || '');
  const [phone, setPhone] = useState<string>(account?.phone || '');
  const [address, setAddress] = useState<string>(account?.address || '');
  const [session_type, setSessionType] = useState<string>('');

  const session = localStorage.getItem(SESSION_STORAGE_KEY);

  useEffect(() => {
    if (session) {
      setSessionType(JSON.parse(session)?.session_type ?? '');
    }
  }, [session]);

  useEffect(() => {
    setName(account?.name || '');
    setEmail(account?.email || '');
    setPhone(account?.phone || '');
    setAddress(account?.address || '');
  }, [account]);

  const updateAccount = useMutation(api.updateAccountApiV1UpdateAccountPost, {
    onSuccess() {
      toast.success('Account updated.');
      accountRefetch();
    },
    onError: apiError,
  });

  const updateIamUser = useMutation(api.updateIamUserApiV1UpdateIamUserPost, {
    onSuccess() {
      toast.success('Account updated.');
      accountRefetch();
    },
    onError: apiError,
  });

  const handleNameSubmit = () => {
    if (account && session_type === 'account') {
      updateAccount.mutate({
        username: account.username,
        name,
      });
    } else {
      updateIamUser.mutate({ name });
    }
  };

  const handleEmailSubmit = () => {
    if (account && session_type === 'account') {
      updateAccount.mutate({
        username: account.username,
        email,
      });
    } else {
      updateIamUser.mutate({ email });
    }
  };

  const handlePhoneSubmit = () => {
    if (account && session_type === 'account') {
      updateAccount.mutate({
        username: account.username,
        phone,
      });
    } else {
      updateIamUser.mutate({ phone });
    }
  };

  const handleAddressSubmit = () => {
    if (account && session_type === 'account') {
      updateAccount.mutate({
        username: account.username,
        address,
      });
    } else {
      updateIamUser.mutate({ address });
    }
  };

  if (isLoading) {
    return <Loading />;
  }
  return (
    <PageContent>
      <PageHeader pageName="My Profile" />
      <Grid container spacing={5}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" elevation={0}>
            <CardRows
              label="Full Name"
              desc="Provide your complete name for accurate identification."
              value={
                <TextField
                  fullWidth
                  size="small"
                  id="name"
                  label="Name"
                  variant="outlined"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              }
              action={
                <Button variant="contained" sx={btnSx} onClick={handleNameSubmit}>
                  Update
                </Button>
              }
            />
            <Divider />
            <CardRows
              label="Email Address"
              desc="Enter your current email address. This will be used for communication and account-related notifications."
              value={
                <TextField
                  fullWidth
                  size="small"
                  id="email"
                  label="Email"
                  variant="outlined"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              }
              action={
                <Button variant="contained" sx={btnSx} onClick={handleEmailSubmit}>
                  Update
                </Button>
              }
            />
            <Divider />
            <CardRows
              label="Phone Number"
              desc="Add your phone number, including country code."
              value={
                <TextField
                  fullWidth
                  size="small"
                  id="name"
                  label="Phone"
                  variant="outlined"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              }
              action={
                <Button variant="contained" sx={btnSx} onClick={handlePhoneSubmit}>
                  Update
                </Button>
              }
            />
            <Divider />
            <CardRows
              label="Physical Address"
              desc="Enter your full street address, including city and state."
              value={
                <TextField
                  fullWidth
                  multiline
                  rows="2"
                  size="small"
                  id="address"
                  label="Address"
                  variant="outlined"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              }
              action={
                <Button variant="contained" sx={btnSx} onClick={handleAddressSubmit}>
                  Update
                </Button>
              }
            />
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" elevation={0}>
            <CardContent>
              <Typography variant="subtitle1">What is My Profile?</Typography>
              <Typography variant="body2">
                Here, you have full control over your personal information. Update your name, phone
                number, and address to keep your profile accurate and up to date. By making changes
                to these fields and saving your updates, you ensure that your contact information is
                current and reflects who you are.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PageContent>
  );
};
